import React, {useMemo} from "react"
import {SchoolClass, SchoolClassCheckIn, ScoreFactorGroup} from "models"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import memo from "set-state-compare/build/memo"
import MoodAverage from "components/score-factor-groups/mood-average"
import PageContentsPresentation from "components/page-contents/presentation"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import ScoreFactorGroupsMoodBarChart from "components/score-factor-groups/mood-bar-chart"
import ScoreFactorGroupsTagCloud from "components/score-factor-groups/tag-cloud"
import useDates from "components/utils/use-dates"
import useModel from "@kaspernj/api-maker/build/use-model"
import useQueryParams from "on-location-changed/build/use-query-params"
import UtilsCard from "components/utils/card"
import {View} from "react-native"

/**
 * @typedef {object} RoutesUserSchoolClassesSchoolClassCheckInsSlide1Props
 * @property {ScoreFactorGroup | undefined} averageScoreFactorGroup
 * @property {ScoreFactorGroup | undefined} barChartScoreFactorGroup
 * @property {string} refreshVersion
 * @property {SchoolClass} schoolClass
 * @property {SchoolClassCheckIn} schoolClassCheckIn
 */

/** @typedef {object} RoutesUserSchoolClassesSchoolClassCheckInsSlide1State */

/**
 * @extends {ShapeComponent<RoutesUserSchoolClassesSchoolClassCheckInsSlide1Props, RoutesUserSchoolClassesSchoolClassCheckInsSlide1State>}
 */
class RoutesUserSchoolClassesSchoolClassCheckInsSlide1 extends ShapeComponent {
  static propTypes = propTypesExact({
    averageScoreFactorGroup: PropTypes.instanceOf(ScoreFactorGroup),
    barChartScoreFactorGroup: PropTypes.instanceOf(ScoreFactorGroup),
    refreshVersion: PropTypes.string.isRequired,
    schoolClass: PropTypes.instanceOf(SchoolClass).isRequired,
    schoolClassCheckIn: PropTypes.instanceOf(SchoolClassCheckIn).isRequired
  })

  setup() {
    const {l} = useDates()
    const queryParams = useQueryParams()
    const {schoolClass, schoolClassCheckIn} = this.p
    const {scoreFactorGroup: tagCloudScoreFactorGroup} = useModel(ScoreFactorGroup, {
      query: ScoreFactorGroup.ransack({identifier_eq: "tag_cloud"}),
      select: {
        ScoreFactorGroup: ["id", "name"]
      }
    })

    const liquidVariables = useMemo(() => ({
      schoolClass,
      schoolClassCheckIn
    }), [schoolClass.id(), schoolClassCheckIn.id()])

    const schoolClassCheckInsQuery = useMemo(
      () => SchoolClassCheckIn
        .ransack({id_eq: schoolClassCheckIn.id()})
        .ransack(queryParams.school_class_check_ins_q),
      [schoolClassCheckIn.id(), queryParams.school_class_check_ins_q]
    )

    this.l = l
    this.liquidVariables = liquidVariables
    this.schoolClassCheckInsQuery = schoolClassCheckInsQuery
    this.tagCloudScoreFactorGroup = tagCloudScoreFactorGroup
  }

  render() {
    const {averageScoreFactorGroup, barChartScoreFactorGroup, refreshVersion, schoolClass, schoolClassCheckIn} = this.p
    const {l, liquidVariables, tagCloudScoreFactorGroup, schoolClassCheckInsQuery} = this.tt

    return (
      <>
        <View dataSet={{component: "routes/user/school-classes/school-class-check-ins/slide-1"}} style={{flexDirection: "row", width: "100%"}}>
          <View style={{width: "25%"}}>
            <PageContentsPresentation identifier="user/school-classes/school-class-check-ins/insights/top-left" liquidVariables={liquidVariables} />
          </View>
          <View style={{width: "50%"}}>
            <PageContentsPresentation identifier="user/school-classes/school-class-check-ins/insights/top-center" liquidVariables={liquidVariables} />
          </View>
          <View style={{width: "25%"}}>
            <PageContentsPresentation identifier="user/school-classes/school-class-check-ins/insights/top-right" liquidVariables={liquidVariables} />
          </View>
        </View>
        <View style={{flexDirection: "row", width: "100%"}}>
          <View style={{width: "25%"}}>
            <MoodAverage
              refreshVersion={refreshVersion}
              schoolClassCheckInsQuery={schoolClassCheckInsQuery}
              scoreFactorGroup={averageScoreFactorGroup}
              style={this.moodAverageStyle ||= {padding: 35}}
              subTitle={l(schoolClassCheckIn.createdAt(), "date-long")}
            />
          </View>
          <View style={{width: "50%", paddingHorizontal: 40}}>
            {schoolClass &&
              <UtilsCard>
                <View style={{width: "calc(100% + 50px)", marginLeft: -50}}>
                  <ScoreFactorGroupsMoodBarChart
                    refreshVersion={refreshVersion}
                    schoolClassCheckInsQuery={this.tt.schoolClassCheckInsQuery}
                    scoreFactorGroup={barChartScoreFactorGroup}
                  />
                </View>
              </UtilsCard>
            }
          </View>
          <View style={{width: "25%"}}>
            {tagCloudScoreFactorGroup &&
              <ScoreFactorGroupsTagCloud
                refreshVersion={refreshVersion}
                schoolClassCheckInsQuery={schoolClassCheckInsQuery}
                scoreFactorGroup={tagCloudScoreFactorGroup}
              />
            }
          </View>
        </View>
      </>
    )
  }
}

export default memo(shapeComponent(RoutesUserSchoolClassesSchoolClassCheckInsSlide1))
